/**
 * Theme Configuration
 * 
 * This file contains the Material UI theme configuration for the application.
 * It defines colors, typography, and component styles using the Gruvbox Dark Hard color scheme.
 */

import { createTheme, responsiveFontSizes } from '@mui/material';

/**
 * Create a dark theme with Gruvbox Dark Hard color scheme
 * The theme follows Material Design principles while using the
 * popular Gruvbox color palette for a distinctive look.
 */
let darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#b8bb26', // Gruvbox green
      light: '#fabd2f', // Gruvbox yellow
      dark: '#98971a', // Gruvbox dark green
    },
    secondary: {
      main: '#83a598', // Gruvbox blue
      light: '#d3869b', // Gruvbox purple
      dark: '#458588', // Gruvbox dark blue
    },
    error: {
      main: '#fb4934', // Gruvbox red
      dark: '#cc241d', // Gruvbox dark red
    },
    warning: {
      main: '#d79921', // Gruvbox yellow
    },
    info: {
      main: '#83a598', // Gruvbox blue
    },
    success: {
      main: '#b8bb26', // Gruvbox green
    },
    background: {
      default: '#1d2021', // Gruvbox dark hard background
      paper: '#282828',  // Gruvbox dark background
    },
    text: {
      primary: '#ebdbb2', // Gruvbox foreground
      secondary: '#a89984', // Gruvbox gray
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '6rem',
      fontWeight: 300,
      letterSpacing: '-0.01562em',
    },
    h2: {
      fontSize: '3.75rem',
      fontWeight: 300,
      letterSpacing: '-0.00833em',
    },
    h3: {
      fontSize: '3rem',
      fontWeight: 400,
      letterSpacing: '0em',
    },
    h4: {
      fontSize: '2.125rem',
      fontWeight: 400,
      letterSpacing: '0.00735em',
    },
    h5: {
      fontSize: '1.5rem',
      fontWeight: 400,
      letterSpacing: '0em',
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: 500,
      letterSpacing: '0.0075em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      letterSpacing: '0.01071em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      letterSpacing: '0.02857em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          // Enhanced glassmorphism for all Paper components with !important to prevent override
          backgroundColor: 'rgba(40, 40, 40, 0.20) !important',
          backdropFilter: 'blur(12px) saturate(180%) !important',
          WebkitBackdropFilter: 'blur(12px) saturate(180%) !important',
          border: '1px solid rgba(235, 219, 178, 0.1) !important',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'rgba(40, 40, 40, 0.30) !important',
            backdropFilter: 'blur(16px) saturate(200%) !important',
            WebkitBackdropFilter: 'blur(16px) saturate(200%) !important',
            borderColor: 'rgba(235, 219, 178, 0.15) !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
      },
    },
  },
});

/**
 * Make fonts responsive across different device sizes
 * This ensures typography scales appropriately on mobile, tablet, and desktop.
 */
darkTheme = responsiveFontSizes(darkTheme);

export default darkTheme;
